import React, {useCallback, useEffect, useMemo} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {useQuery} from '@apollo/client'
import {GET_ALL_CAMPAIGNS} from '../utils/api/campaign'
import {
  GET_ALL_ADVERTISERS_FOR_FILTER,
  createAdvertiserFilters,
} from '../utils/advertiser-helpers'
import Table from '../components/lists/Table/Table'
import Breadcrumb from '../components/navigation/Breadcrumb'
import TabBarControlled from '../components/navigation/TabBarControlled'
import {CampaignType, CampaignStatus} from '../__generated__/graphql'
import {useCampaignsWithCost} from '../utils/api/cube'
import {useQueryVariables} from '../utils/hooks'
import {getMarketplaceColumns} from '../utils/campaign-utils'
import {trackEvent} from '../utils/analytics'
import {safeInt, sameArray} from '../utils/helpers'
import { setOrDelete } from './CampaignsTest'

const pages = [{name: 'Marketplace Campaigns', href: '/marketplace-campaigns', current: true}]
const PAGE_SIZE_OPTIONS = ['10', '25', '50', '100']

const MarketplaceCampaigns = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Get active tab from URL, default to 'all'
  const activeTab = searchParams.get('tab') || 'all'

  const defaultFilters = {
    advertiserId: searchParams.getAll('advertiserId'),
    search: searchParams.get('search') || '',
  }

  // Extract sort information from URL parameters
  const defaultSort = useMemo(() => ({
    column: searchParams.get('sortColumn') || 'startDate',
    direction: searchParams.get('sortDirection') || 'DESC'
  }), [searchParams])

  // Read pagination from URL with safe parsing
  const defaultPagination = {
    current: safeInt(searchParams.get('page'), 1),
    pageSize: safeInt(searchParams.get('pageSize'), 10),
  }

  const {variables, setPagination, setFilters, setSort} =
    useQueryVariables(defaultFilters, defaultSort, defaultPagination)

  // Build query variables with marketplace-specific filters
  const queryVariables = useMemo(() => {
    const filters: any = {
      ...variables.filters,
      type: [CampaignType.Standard, CampaignType.MarketplaceNativeDisplay],
      notOwned: true,
    }

    // Add status filter based on active tab
    if (activeTab === 'pending') {
      filters.awaitingApproval = true
    } else if (activeTab === 'active') {
      filters.status = [CampaignStatus.Active]
    }
    // 'all' tab doesn't add a status filter

    return {
      ...variables,
      filters,
    }
  }, [variables, activeTab])

  const {loading, data} = useQuery(GET_ALL_CAMPAIGNS, {
    variables: queryVariables,
  })

  // Reset pagination if on empty page or beyond total pages
  useEffect(() => {
    const items = data?.campaigns?.items || []
    const totalPages = data?.campaigns?.meta?.totalPages || 1
    const currentPage = variables.page || 1

    if (!loading && currentPage > 1 && (items.length === 0 || currentPage > totalPages)) {
      setPagination({
        current: Math.max(1, totalPages),
        pageSize: variables.perPage || 10,
      })
    }
  }, [loading, data, variables.page, variables.perPage, setPagination])

  const campaigns = useMemo(() => data?.campaigns?.items || [], [data])
  const campaignsWithCost = useCampaignsWithCost(campaigns)

  // Get advertiser filters
  const {data: advertisersData} = useQuery(GET_ALL_ADVERTISERS_FOR_FILTER, {
    variables: {
      perPage: 1000,
    },
  })

  const advertiserFilters = useMemo(() => {
    if (advertisersData) {
      return createAdvertiserFilters(advertisersData)
    }
    return []
  }, [advertisersData])

  const navigate = useNavigate()

  const columns = getMarketplaceColumns(
    advertiserFilters,
    defaultFilters,
    navigate
  )

  const handleRowClicked = (record: any) => {
    navigate(`/marketplace-campaigns/${record._id}/websites`)
  }

  // Handle tab change
  const handleTabChange = (key: string) => {
    const urlParams = new URLSearchParams(searchParams)

    // Update tab parameter
    if (key === 'all') {
      urlParams.delete('tab')
    } else {
      urlParams.set('tab', key)
    }

    // Reset to first page when changing tabs
    urlParams.delete('page')

    setSearchParams(urlParams, { replace: true })
  }

  // Handle table changes (pagination, filters, sorting)
  const handleTableChange = useCallback((pagination, filters, sorter) => {
    const nextPageSize = pagination?.pageSize || variables.perPage || 10
    const isPageSizeChange = nextPageSize !== variables.perPage

    const updatedFilters = {
      advertiserId: filters.advertiserId || [],
      search: filters.search || '',
    }

    // Check if filters have changed
    const hasFiltersChanged =
      !sameArray(updatedFilters.advertiserId, (variables.filters as any).advertiserId || []) ||
      updatedFilters.search !== ((variables.filters as any).search || '')

    // Check if sort changed
    const hasSortChanged = sorter?.field && (
      sorter.field !== variables.sort.column ||
      (sorter.order === 'ascend' ? 'ASC' : 'DESC') !== variables.sort.direction
    )

    // Reset to page 1 if page size, filters, or sort changed
    const nextPage = (isPageSizeChange || hasFiltersChanged || hasSortChanged)
      ? 1
      : pagination?.current || 1

    setPagination({
      current: nextPage,
      pageSize: nextPageSize,
    })

    const urlParams = new URLSearchParams(searchParams)

    // Pagination
    setOrDelete(urlParams, 'page', nextPage, 1)
    setOrDelete(urlParams, 'pageSize', nextPageSize, 10)

    // Filters
    urlParams.delete('advertiserId')
    updatedFilters.advertiserId?.forEach(a => urlParams.append('advertiserId', String(a)))
    setOrDelete(urlParams, 'search', updatedFilters.search, '')

    // Handle sorting
    if (sorter && sorter.field) {
      const sortDirection = sorter.order === 'ascend' ? 'ASC' : 'DESC'
      const sortColumn = sorter.field

      urlParams.set('sortColumn', String(sortColumn))
      urlParams.set('sortDirection', String(sortDirection))

      setSort({
        column: sortColumn,
        direction: sortDirection
      })
    } else {
      urlParams.set('sortColumn', String(variables.sort.column))
      urlParams.set('sortDirection', String(variables.sort.direction))
    }

    // Smart history management
    const historyMode = (isPageSizeChange || nextPage !== variables.page)
      ? { replace: true }
      : {}

    setSearchParams(urlParams, historyMode)
    setFilters(updatedFilters)

    // Analytics
    trackEvent('marketplace_campaigns_pagination_changed', {
      trigger: isPageSizeChange ? 'pageSize'
        : hasFiltersChanged ? 'filters'
        : hasSortChanged ? 'sort'
        : 'page',
      page: nextPage,
      pageSize: nextPageSize,
      totalItems: data?.campaigns?.meta?.totalItems,
    })
  }, [searchParams, variables, setSearchParams, setPagination, setFilters, setSort, data])

  const pageSizeOptions = useMemo(() => {
    const options = [...PAGE_SIZE_OPTIONS]
    const currentSize = String(variables.perPage || 10)
    if (!options.includes(currentSize)) {
      options.push(currentSize)
    }
    return options
  }, [variables.perPage])

  const tabItems = [
    {
      key: 'pending',
      name: 'Pending approval',
    },
    {
      key: 'active',
      name: 'Active Campaigns',
    },
    {
      key: 'all',
      name: 'All Campaigns',
    },
  ]

  return (
    <div>
      <div className='mb-4'>
        <Breadcrumb pages={pages} />
      </div>

      <TabBarControlled
        tabs={tabItems}
        value={activeTab}
        onChange={handleTabChange}
        className="mb-4"
      />

      <Table
        columns={columns}
        data={campaignsWithCost}
        rowKey="_id"
        onRowClicked={handleRowClicked}
        loading={loading}
        className="marketplace-campaigns-table"
        pagination={{
          total: data ? data.campaigns.meta.totalItems : 0,
          pageSize: variables.perPage,
          current: variables.page,
          showSizeChanger: true,
          pageSizeOptions,
        }}
        defaultFilters={defaultFilters}
        onChange={handleTableChange}
      />
    </div>
  )
}

export default MarketplaceCampaigns
